import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react"
import { createPortal } from "react-dom"

// Foreground routes render inside their <Route> (so they keep its match), and
// portal their chrome into these slots, which App positions in the layout.
type OverlaySlotName = "modal" | "cards"

type OverlaySlotsContextValue = {
  nodes: Partial<Record<OverlaySlotName, HTMLElement | null>>
  setNode: (slot: OverlaySlotName, node: HTMLElement | null) => void
}

const OverlaySlotsContext = createContext<OverlaySlotsContextValue | null>(
  null
)

export function OverlaySlotsProvider({
  children
}: {
  children: React.ReactNode
}) {
  const [nodes, setNodes] = useState<OverlaySlotsContextValue["nodes"]>({})

  const setNode = useCallback<OverlaySlotsContextValue["setNode"]>(
    (slot, node) =>
      setNodes((prev) =>
        prev[slot] === node ? prev : { ...prev, [slot]: node }
      ),
    []
  )

  const value = useMemo(() => ({ nodes, setNode }), [nodes, setNode])

  return (
    <OverlaySlotsContext.Provider value={value}>
      {children}
    </OverlaySlotsContext.Provider>
  )
}

export function OverlaySlot({
  slot,
  className,
  style
}: {
  slot: OverlaySlotName
  className?: string
  style?: React.CSSProperties
}) {
  const setNode = useContext(OverlaySlotsContext)?.setNode
  const ref = useCallback(
    (node: HTMLDivElement | null) => setNode?.(slot, node),
    [setNode, slot]
  )

  return <div ref={ref} className={className} style={style} />
}

export function OverlayPortal({
  slot,
  children
}: {
  slot: OverlaySlotName
  children: React.ReactNode
}) {
  const node = useContext(OverlaySlotsContext)?.nodes[slot]
  return node ? createPortal(children, node) : null
}
